import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Category } from '@fcom/dapi/api/models';
import { Amount, ServiceCatalogService, ServiceCatalogServices } from '@fcom/dapi/interfaces';

import { getBundleConstituents, isAncillaryBundle } from '../../modules/ancillaries/utils';
import { CommonBookingFeatureState } from '../store.interface';
import { serviceCategoriesMapSelector } from './services.selector';

/**
 * These non-seat (ancillary+ancillary) bundle selectors read the cart service catalog through
 * serviceCategoriesMapSelector (category → services). They live here rather than in bundle.selector.ts to
 * avoid a cycle: services.selector already depends on bundle.selector, so bundle.selector cannot import
 * from services.selector.
 *
 * serviceCategoriesMapSelector is referenced through this wrapper (rather than passed to createSelector
 * directly) so the binding is read lazily at selection time. This file is re-entered through the store
 * barrel while services.selector is still evaluating, so a direct top-level reference would capture
 * `undefined`. The wrapped selector stays memoized, so the outer selectors recompute only when it does.
 */
const catalogServicesByCategory = (state: CommonBookingFeatureState) => serviceCategoriesMapSelector(state);

export interface NonSeatBundleAvailability {
  availableInCatalog: boolean;
  /** Discounted price of the whole bundle (from the catalog item's totalPrice). */
  bundleTotalPrice: Amount | null;
  /** Pre-discount price, i.e. the items' combined price without the bundle discount (originalPrice). */
  itemsWithoutBundlePrice: Amount | null;
  /** The raw bundle catalog item, needed to build the selections entry when the passenger adds the combo. */
  bundleItem: ServiceCatalogService;
}

/** { [bundleCategory]: { [flightId]: { [passengerId]: NonSeatBundleAvailability } } } */
export type NonSeatBundlesByFlightAndPassenger = Partial<
  Record<Category, Record<string, Record<string, NonSeatBundleAvailability>>>
>;

/**
 * Returns, for each non-seat (ancillary+ancillary) bundle category present in the cart service catalog,
 * the availability and pricing per flight and per passenger.
 *
 * Prices are taken from the per-flight/per-passenger catalog service item (totalPrice / originalPrice)
 * rather than lowestPrice, which may aggregate across flights and passengers.
 *
 * Categories, flights and passengers with no offered bundle item are omitted, so a present key can be
 * treated as "available".
 */
export const nonSeatBundlesByFlightAndPassengerSelector: MemoizedSelector<
  CommonBookingFeatureState,
  NonSeatBundlesByFlightAndPassenger
> = createSelector(catalogServicesByCategory, (categoriesMap): NonSeatBundlesByFlightAndPassenger =>
  (Object.keys(categoriesMap ?? {}) as Category[])
    .filter((category) => isAncillaryBundle(category))
    .reduce((byCategory, category) => {
      const byFlight = Object.entries(categoriesMap[category] ?? {}).reduce(
        (flightAcc, [flightId, servicesByPax]) => {
          const byPassenger = Object.entries(servicesByPax ?? {}).reduce(
            (paxAcc, [passengerId, items]) => {
              const bundleItem = items?.[0];
              if (!bundleItem) {
                return paxAcc;
              }
              paxAcc[passengerId] = {
                availableInCatalog: true,
                bundleTotalPrice: bundleItem.totalPrice ?? null,
                itemsWithoutBundlePrice: bundleItem.originalPrice ?? null,
                bundleItem,
              };
              return paxAcc;
            },
            {} as Record<string, NonSeatBundleAvailability>
          );
          if (Object.keys(byPassenger).length) {
            flightAcc[flightId] = byPassenger;
          }
          return flightAcc;
        },
        {} as Record<string, Record<string, NonSeatBundleAvailability>>
      );
      if (Object.keys(byFlight).length) {
        byCategory[category] = byFlight;
      }
      return byCategory;
    }, {} as NonSeatBundlesByFlightAndPassenger)
);

/**
 * The resolved constituent standalone catalog services for a bundle, in display order. A constituent
 * whose standalone catalog service is not shipped is omitted, so this can have fewer than two entries.
 *
 * { [bundleCategory]: { [flightId]: ServiceCatalogService[] } }
 */
export type NonSeatBundleConstituentsByFlight = Partial<Record<Category, Record<string, ServiceCatalogService[]>>>;

/** Splits a bundle variant like "BDL_LOUS_CAOB" into its constituent variant codes ["LOUS", "CAOB"]. */
const bundleVariantCodes = (variant?: string): string[] =>
  (variant?.split('_') ?? []).filter((code) => code && code !== 'BDL');

const resolveConstituentService = (
  childServices: ServiceCatalogServices | undefined,
  flightId: string,
  variantCodes: string[]
): ServiceCatalogService | null => {
  // Flight-based categories (lounge, wifi, meal, priority) are keyed by flight id; bound-based ones
  // (cabin baggage) by the single-segment bound id BND-<flightId>.
  const servicesByPax = childServices?.[flightId] ?? childServices?.[`BND-${flightId}`];
  if (!servicesByPax) {
    return null;
  }
  const allServices = Object.values(servicesByPax).flat();
  // Prefer the service the bundle is actually built from (variant match); fall back to the first offered.
  return allServices.find((service) => variantCodes.includes(service.variant)) ?? allServices[0] ?? null;
};

/**
 * Per-flight resolved constituent standalone catalog services for each ancillary+ancillary (non-seat) bundle.
 *
 * Keyed by flight (not passenger): the constituents are identical for all passengers on a flight but may
 * differ between flights (e.g. a different meal per flight), which is why this is separate from the
 * per-passenger availability/price selector. The bundle's name/description are per-category (not per-flight)
 * and come from translationsForCategorySelector, so they are not included here.
 */
export const nonSeatBundleConstituentsByFlightSelector: MemoizedSelector<
  CommonBookingFeatureState,
  NonSeatBundleConstituentsByFlight
> = createSelector(catalogServicesByCategory, (categoriesMap): NonSeatBundleConstituentsByFlight =>
  (Object.keys(categoriesMap ?? {}) as Category[])
    .filter((category) => isAncillaryBundle(category))
    .reduce((byCategory, bundleCategory) => {
      const constituentCategories = getBundleConstituents(bundleCategory);
      if (!constituentCategories) {
        return byCategory;
      }
      const byFlight = Object.entries(categoriesMap[bundleCategory] ?? {}).reduce(
        (flightAcc, [flightId, servicesByPax]) => {
          const bundleItem = Object.values(servicesByPax ?? {}).flat()[0];
          const variantCodes = bundleVariantCodes(bundleItem?.variant);
          flightAcc[flightId] = constituentCategories
            .map((childCategory) => resolveConstituentService(categoriesMap[childCategory], flightId, variantCodes))
            .filter((service): service is ServiceCatalogService => !!service);
          return flightAcc;
        },
        {} as Record<string, ServiceCatalogService[]>
      );
      if (Object.keys(byFlight).length) {
        byCategory[bundleCategory] = byFlight;
      }
      return byCategory;
    }, {} as NonSeatBundleConstituentsByFlight)
);
